/**
 * StrategyVersionDiffDialog — compare two strategy versions (Spec A / D51, AC-A6-3).
 *
 * Loads two version snapshots and renders their structural diff (blocks +
 * screens + variables, added/removed/changed) using the same action vocabulary
 * and colours as the strategy<->onboarding sync plan view, so the whole strategy
 * surface reads consistently. Read-only: this is a comparison, not a resolver.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { StrategyVersion } from '@segmently/schema-models/strategy';
import type { StrategyVersionSnapshot } from '@modules/firebase-core/services/StrategyVersionService';
import {
  diffStrategyVersions,
  type StrategyVersionDiff,
  type VersionDiffAction,
} from '@modules/strategy-blocks/utils/strategyVersionDiff';

export interface StrategyVersionDiffDialogProps {
  open: boolean;
  onClose: () => void;
  versions: StrategyVersion[];
  getSnapshot: (versionId: string) => Promise<StrategyVersionSnapshot | null>;
}

// Shared visual vocabulary with StrategySyncPlanView (create/replace/preserve/delete).
const ACTION_META: Record<VersionDiffAction, { label: string; color: 'success' | 'warning' | 'default' | 'error' }> = {
  added: { label: 'added', color: 'success' },
  changed: { label: 'changed', color: 'warning' },
  removed: { label: 'removed', color: 'error' },
  unchanged: { label: 'unchanged', color: 'default' },
};

const HEAD_CELL_SX = { fontWeight: 700, bgcolor: 'action.hover' } as const;

function ActionChip({ action }: { action: VersionDiffAction }): React.ReactElement {
  const meta = ACTION_META[action];
  return (
    <Chip
      size="small"
      color={meta.color}
      variant={action === 'unchanged' ? 'outlined' : 'filled'}
      label={meta.label}
    />
  );
}

export const StrategyVersionDiffDialog: React.FC<StrategyVersionDiffDialogProps> = ({
  open,
  onClose,
  versions,
  getSnapshot,
}) => {
  // Default: base = second-newest, target = newest (versions are newest-first).
  const [baseId, setBaseId] = useState<string>('');
  const [targetId, setTargetId] = useState<string>('');
  const [diff, setDiff] = useState<StrategyVersionDiff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    // Spec C (C6): default to LINEAGE — newest vs its parentVersion node
    // ("what moved in vN"), falling back to the two newest when the parent
    // is unknown or absent from the list.
    const target = versions[0];
    const parentId = target?.parentVersion && versions.some((version) => version.versionId === target.parentVersion)
      ? target.parentVersion
      : undefined;
    setTargetId((prev) => prev || target?.versionId || '');
    setBaseId((prev) => prev || parentId || versions[1]?.versionId || versions[0]?.versionId || '');
  }, [open, versions]);

  useEffect(() => {
    if (!open || !baseId || !targetId) {
      setDiff(null);
      return;
    }
    if (baseId === targetId) {
      setDiff(null);
      setError('Pick two different versions to compare.');
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([getSnapshot(baseId), getSnapshot(targetId)])
      .then(([base, target]) => {
        if (!active) return;
        if (!base || !target) {
          setError('One of the version snapshots could not be loaded.');
          setDiff(null);
          return;
        }
        setDiff(diffStrategyVersions(base, target));
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : 'Failed to compute diff'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, baseId, targetId, getSnapshot]);

  const changedVariables = useMemo(
    () => (diff?.variables ?? []).filter((v) => v.action !== 'unchanged'),
    [diff],
  );

  const renderPicker = useCallback(
    (value: string, onChange: (id: string) => void, testid: string) => (
      <Select
        size="small"
        value={value}
        onChange={(e) => onChange(String(e.target.value))}
        inputProps={{ 'data-testid': testid }}
        sx={{ minWidth: 160 }}
      >
        {versions.map((v) => (
          <MenuItem key={v.versionId} value={v.versionId}>
            {v.name ?? v.versionId}
          </MenuItem>
        ))}
      </Select>
    ),
    [versions],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="strategy-version-diff-dialog">
      <DialogTitle>Compare strategy versions</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              Base
            </Typography>
            {renderPicker(baseId, setBaseId, 'strategy-version-diff-base')}
            <Typography variant="body2" color="text.secondary">
              →
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Target
            </Typography>
            {renderPicker(targetId, setTargetId, 'strategy-version-diff-target')}
          </Stack>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && diff && (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="strategy-version-diff-counts">
                <Chip size="small" color="success" variant="filled" label={`${diff.counts.added} added`} />
                <Chip size="small" color="warning" variant="filled" label={`${diff.counts.changed} changed`} />
                <Chip size="small" color="error" variant="filled" label={`${diff.counts.removed} removed`} />
                <Chip size="small" variant="outlined" label={`${diff.counts.unchanged} unchanged`} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${diff.changedScreenKeys.length} screen change${diff.changedScreenKeys.length === 1 ? '' : 's'}`}
                />
              </Stack>

              <TableContainer sx={{ maxHeight: 360, overflowX: 'auto' }}>
                <Table size="small" stickyHeader data-testid="strategy-version-diff-table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...HEAD_CELL_SX, width: '45%' }}>Block</TableCell>
                      <TableCell sx={HEAD_CELL_SX}>Action</TableCell>
                      <TableCell sx={HEAD_CELL_SX}>Screen changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diff.blocks.map((block) => (
                      <TableRow key={block.blockId} data-testid={`strategy-version-diff-block-${block.blockId}`}>
                        <TableCell>
                          <Typography variant="body2" component="span">
                            {block.blockName || block.blockId}
                          </Typography>
                          {block.blockName && (
                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                              {block.blockId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <ActionChip action={block.action} />
                        </TableCell>
                        <TableCell>
                          {block.changedScreenKeys.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {block.screens
                                .filter((s) => s.action !== 'unchanged')
                                .map((s) => (
                                  <Chip
                                    key={s.screenId}
                                    size="small"
                                    variant="outlined"
                                    color={ACTION_META[s.action].color}
                                    label={`${s.screenName || s.screenId}: ${s.action}`}
                                  />
                                ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {changedVariables.length > 0 && (
                <Stack spacing={1} data-testid="strategy-version-diff-variables">
                  <Typography variant="subtitle2">
                    Variables · {changedVariables.length} change{changedVariables.length === 1 ? '' : 's'}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {changedVariables.map((v) => (
                      <Chip
                        key={v.variableId}
                        size="small"
                        variant="outlined"
                        color={ACTION_META[v.action].color}
                        label={`${v.name || v.variableId}: ${v.action}`}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StrategyVersionDiffDialog;
